/**
 * Supabase client wrapper.
 *
 * The Margin free tier is fully offline. Supabase is only invoked when
 * the user opts into Pro features (cloud-backed sync,
 * couples sync). This wrapper:
 *   - Returns a real client when VITE_SUPABASE_URL + VITE_SUPABASE_ANON_KEY
 *     are both set in the environment.
 *   - Returns null otherwise, so a build without Supabase configured
 *     compiles + runs cleanly. UI consumers check `isSupabaseConfigured()`
 *     before assuming the client exists.
 *
 * Sessions persist in localStorage via the SDK's default storage; the
 * tab listens for cross-tab auth state changes via storage events.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const supabase: SupabaseClient | null =
  url && anonKey
    ? createClient(url, anonKey, {
        auth: {
          // Persist + detect session in URL so magic-link callbacks
          // route the user back into an authenticated state on return.
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: true,
        },
      })
    : null;

/** True when both env vars are set and the SDK was instantiated. */
export function isSupabaseConfigured(): boolean {
  return supabase !== null;
}

/**
 * Throws if the client wasn't configured. Use inside Pro-feature code
 * that assumes Supabase is reachable; the UI should gate on
 * isSupabaseConfigured() before reaching this.
 */
export function requireSupabase(): SupabaseClient {
  if (!supabase) {
    throw new Error(
      'Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env',
    );
  }
  return supabase;
}
